import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Stack,
} from '@mui/material';

export default function AddressEditDialog({
  open,
  onClose,
  onSaved,
  orgId,
  address,
  addresses = [],
  tokens,
  apiBase = process.env.REACT_APP_PORTAL_API_URL || ''
}) {
  const [form, setForm] = useState(() => ({
    street_1: address?.street_1 || '',
    street_2: address?.street_2 || '',
    city: address?.city || '',
    state: address?.state || '',
    zip_code: address?.zip_code || '',
  }));
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Reset form when address changes
  React.useEffect(() => {
    setForm({
      street_1: address?.street_1 || '',
      street_2: address?.street_2 || '',
      city: address?.city || '',
      state: address?.state || '',
      zip_code: address?.zip_code || '',
    });
    setErrors({});
  }, [address?.id]);

  const canSave = useMemo(() => {
    return (
      form.street_1.trim() &&
      form.city.trim() &&
      form.state.trim() &&
      form.zip_code.trim()
    );
  }, [form]);

  const copyFrom = (purpose) => {
    const src = addresses.find(a => a?.purpose === purpose);
    if (!src) return;
    setForm({
      street_1: src.street_1 || '',
      street_2: src.street_2 || '',
      city: src.city || '',
      state: src.state || '',
      zip_code: src.zip_code || '',
    });
  };

  const save = async () => {
    const newErrors = {};
    if (!form.street_1.trim()) newErrors.street_1 = 'Required';
    if (!form.city.trim()) newErrors.city = 'Required';
    if (!form.state.trim()) newErrors.state = 'Required';
    if (!form.zip_code.trim()) newErrors.zip_code = 'Required';
    setErrors(newErrors);
    if (Object.keys(newErrors).length) return;
    setSaving(true);
    try {
      const res = await fetch(`${apiBase}/organizations/${orgId}/addresses/${address.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens?.id_token}`,
        },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('Failed to save address');
      const updated = await res.json();
      onSaved && onSaved(updated);
    } catch (e) {
      // simple error surfacing; could use snackbar
      setErrors(prev => ({ ...prev, _root: e.message }));
    } finally {
      setSaving(false);
    }
  };

  const otherPurposes = ['corporate', 'billing', 'shipping'].filter(p => p !== address?.purpose);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Edit {address?.purpose?.charAt(0).toUpperCase() + address?.purpose?.slice(1)} Address
      </DialogTitle>
      <DialogContent dividers>
        {errors._root && (
          <Box sx={{ color: 'error.main', mb: 2 }}>{errors._root}</Box>
        )}
        <Stack spacing={2}>
          <TextField
            label="Street Address 1"
            value={form.street_1}
            onChange={e => setForm({ ...form, street_1: e.target.value })}
            required
            error={!!errors.street_1}
            helperText={errors.street_1}
          />
          <TextField
            label="Street Address 2"
            value={form.street_2}
            onChange={e => setForm({ ...form, street_2: e.target.value })}
          />
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="City"
              value={form.city}
              onChange={e => setForm({ ...form, city: e.target.value })}
              required
              error={!!errors.city}
              helperText={errors.city}
              fullWidth
            />
            <TextField
              label="State"
              value={form.state}
              onChange={e => setForm({ ...form, state: e.target.value })}
              required
              error={!!errors.state}
              helperText={errors.state}
              fullWidth
            />
            <TextField
              label="Zip Code"
              value={form.zip_code}
              onChange={e => setForm({ ...form, zip_code: e.target.value })}
              required
              error={!!errors.zip_code}
              helperText={errors.zip_code}
              fullWidth
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            {otherPurposes.map((p) => (
              <Button
                key={p}
                variant="outlined"
                size="small"
                onClick={() => copyFrom(p)}
                disabled={!addresses.find(a => a?.purpose === p)}
              >
                Copy from {p}
              </Button>
            ))}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={save} variant="contained" disabled={!canSave || saving}>Save</Button>
      </DialogActions>
    </Dialog>
  );
}

